import { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useMockAuth } from '../../hooks/useMockAuth';
import { useTheme } from '../../contexts/ThemeContext';

interface AdminLayoutProps {
  children: ReactNode;
}

/**
 * AdminLayout - Layout for the Admin section
 * Includes sidebar navigation for admin-only features
 */
export function AdminLayout({ children }: AdminLayoutProps) {
  const { user } = useMockAuth();
  const { theme } = useTheme();

  const colors = {
    dark: {
      sidebarBg: '#1a1a2e',
      sidebarBorder: '#2d2d44',
      mainBg: '#0a0a0f',
      cardBg: 'rgba(26, 26, 46, 0.6)',
      cardBorder: 'rgba(220, 38, 38, 0.3)',
      text: '#ffffff',
      textMuted: '#9ca3af',
      linkColor: '#d1d5db',
      linkHover: '#2d2d44',
      activeLink: '#dc2626',
      headerColor: '#ef4444'
    },
    light: {
      sidebarBg: '#f9fafb',
      sidebarBorder: '#e5e7eb',
      mainBg: '#ffffff',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280',
      linkColor: '#4b5563',
      linkHover: '#f3f4f6',
      activeLink: '#dc2626',
      headerColor: '#dc2626'
    }
  };

  const c = colors[theme];

  return (
    <div style={{ display: 'flex', minHeight: '100%' }}>
      {/* Sidebar */}
      <aside style={{
        width: '16rem',
        backgroundColor: c.sidebarBg,
        borderRight: `1px solid ${c.sidebarBorder}`,
        padding: '1.5rem'
      }}>
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: c.headerColor,
            marginBottom: '0.5rem'
          }}>
            Admin Panel
          </h2>
          <p style={{
            fontSize: '0.875rem',
            color: c.textMuted,
            margin: 0
          }}>
            {user?.email}
          </p>
        </div>

        <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <Link
            to="/admin"
            style={{
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              color: c.linkColor,
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            activeProps={{
              style: {
                backgroundColor: c.activeLink,
                color: 'white'
              }
            }}
          >
            📊 Overview
          </Link>

          <Link
            to="/admin/users"
            style={{
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              color: c.linkColor,
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            activeProps={{
              style: {
                backgroundColor: c.activeLink,
                color: 'white'
              }
            }}
          >
            👥 User Management
          </Link>

          <Link
            to="/admin/products"
            style={{
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              color: c.linkColor,
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            activeProps={{
              style: {
                backgroundColor: c.activeLink,
                color: 'white'
              }
            }}
          >
            🎸 Product Management
          </Link>

          <Link
            to="/admin/licenses"
            style={{
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              color: c.linkColor,
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            activeProps={{
              style: {
                backgroundColor: c.activeLink,
                color: 'white'
              }
            }}
          >
            🔑 License Oversight
          </Link>

          <Link
            to="/admin/analytics"
            style={{
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              color: c.linkColor,
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            activeProps={{
              style: {
                backgroundColor: c.activeLink,
                color: 'white'
              }
            }}
          >
            📈 Analytics & Reports
          </Link>

          <Link
            to="/admin/settings"
            style={{
              padding: '0.75rem 1rem',
              borderRadius: '0.375rem',
              color: c.linkColor,
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            activeProps={{
              style: {
                backgroundColor: c.activeLink,
                color: 'white'
              }
            }}
          >
            ⚙️ System Settings
          </Link>
        </nav>

        {/* System Status */}
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          background: c.cardBg,
          borderRadius: '0.5rem',
          border: `1px solid ${c.cardBorder}`,
          backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
        }}>
          <h3 style={{
            fontSize: '0.75rem',
            fontWeight: '600',
            color: c.headerColor,
            textTransform: 'uppercase',
            marginBottom: '0.75rem'
          }}>
            System Status
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
              <span style={{ color: c.textMuted }}>Total Users</span>
              <span style={{ fontWeight: '600', color: c.text }}>0</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
              <span style={{ color: c.textMuted }}>Active Products</span>
              <span style={{ fontWeight: '600', color: c.text }}>0</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
              <span style={{ color: c.textMuted }}>Total Revenue</span>
              <span style={{ fontWeight: '600', color: '#059669' }}>$0.00</span>
            </div>
            <div style={{
              marginTop: '0.5rem',
              paddingTop: '0.5rem',
              borderTop: `1px solid ${c.cardBorder}`,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.75rem'
            }}>
              <span style={{
                width: '0.5rem',
                height: '0.5rem',
                borderRadius: '50%',
                backgroundColor: '#10b981'
              }}></span>
              <span style={{ color: c.textMuted }}>All systems operational</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div style={{ flex: 1, backgroundColor: c.mainBg }}>
        {children}
      </div>
    </div>
  );
}
